import { useState, useMemo } from 'react';
import { Headphones, MapPin, Bell, Search } from 'lucide-react';
import { ModuleHero } from '../components/ModuleHero';
import { useStore } from '../hooks/useStore';
import { listAnnouncements } from '../services/communicationHubService';

export function FrontDeskPage() {
  const db = useStore((d) => d);
  const tournaments = useMemo(
    () => [...db.tournaments].filter((t) => t.status !== 'archived').sort((a, b) => b.startsAt.localeCompare(a.startsAt)),
    [db.tournaments],
  );
  const [tournamentId, setTournamentId] = useState<string>(() => tournaments[0]?.id ?? '');
  const [query, setQuery] = useState('');

  const tournament = tournaments.find((t) => t.id === tournamentId);

  const announcements = tournament ? listAnnouncements(tournament.id) : [];
  const venues = tournament
    ? db.venues.filter((v) => v.id === tournament.primaryVenueId || db.tournaments.some((t) => t.primaryVenueId === v.id))
    : db.venues;
  const games = tournament ? db.games.filter((g) => g.tournamentId === tournament.id) : [];
  const slotById = new Map(db.scheduleSlots.map((s) => [s.id, s] as const));
  const teams = tournament ? db.teams.filter((t) => t.tournamentId === tournament.id) : [];

  const liveCount = games.filter((g) => g.status === 'in_progress').length;
  const upcomingCount = games.filter((g) => g.status === 'scheduled' || g.status === 'ready').length;

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return games
      .filter((g) => {
        const home = teams.find((t) => t.id === g.homeTeamId)?.name?.toLowerCase() ?? '';
        const away = teams.find((t) => t.id === g.awayTeamId)?.name?.toLowerCase() ?? '';
        return home.includes(q) || away.includes(q);
      })
      .slice(0, 10);
  }, [query, games, teams]);

  return (
    <>
      <ModuleHero
        eyebrow="Experience"
        title="Front Desk — answers without lines."
        description="Schedule lookup, venue maps, where's-my-game, and pinned announcements."
        icon={Headphones}
        accent="arc"
        status={
          liveCount > 0
            ? { label: 'Live', tone: 'ok' }
            : upcomingCount > 0
              ? { label: `${upcomingCount} games coming`, tone: 'info' }
              : undefined
        }
      >
        {tournaments.length > 1 && (
          <select className="select" value={tournamentId} onChange={(e) => setTournamentId(e.target.value)} style={{ maxWidth: 240 }}>
            {tournaments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </ModuleHero>

      {!tournament ? (
        <div className="empty">Pick a season or tournament above.</div>
      ) : (
        <>
          <div className="card">
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <Search size={16} color="var(--text-muted)" />
              <input
                className="input"
                placeholder='Try "Ice Breakers" or a player name'
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ flex: 1 }}
              />
            </div>
            {searchResults && (
              <div className="stack" style={{ marginTop: 12 }}>
                {searchResults.length === 0 && <div className="muted">No matches.</div>}
                {searchResults.map((g) => {
                  const home = teams.find((t) => t.id === g.homeTeamId);
                  const away = teams.find((t) => t.id === g.awayTeamId);
                  const slot = g.scheduleSlotId ? slotById.get(g.scheduleSlotId) : null;
                  return (
                    <div key={g.id} className="row row--between" style={{ borderBottom: '1px solid var(--surface-line)', paddingBottom: 6 }}>
                      <div>
                        <strong>{home?.name}</strong> vs <strong>{away?.name}</strong>
                        <div className="muted" style={{ fontSize: 12 }}>{slot ? new Date(slot.startsAt).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : 'Time TBD'}</div>
                      </div>
                      <span className="chip">{g.status.replace('_', ' ')}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {announcements.length > 0 && (
            <div className="card card--accent">
              <div className="card__head">
                <h3 className="card__title">
                  <Bell size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
                  Announcements
                </h3>
              </div>
              <div className="stack">
                {announcements.map((a) => (
                  <div key={a.id}>
                    <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                      <strong>{a.title}</strong>
                      <span className={`chip chip--${a.severity === 'urgent' ? 'err' : a.severity === 'warning' ? 'warn' : a.severity === 'success' ? 'ok' : 'info'}`}>{a.severity}</span>
                    </div>
                    <p className="muted" style={{ marginTop: 4 }}>{a.body}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid--2">
            <div className="card">
              <div className="card__head">
                <h3 className="card__title">
                  <MapPin size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
                  Venues
                </h3>
              </div>
              {venues.length === 0 ? (
                <div className="empty">No venues yet.</div>
              ) : (
                <div className="stack">
                  {venues.map((v) => {
                    const venueRinks = db.rinks.filter((r) => r.venueId === v.id);
                    return (
                      <div key={v.id} className="row row--between" style={{ borderBottom: '1px solid var(--surface-line)', paddingBottom: 6 }}>
                        <div>
                          <strong>{v.name}</strong>
                          <div className="muted" style={{ fontSize: 12 }}>{v.address}, {v.city} {v.region}</div>
                        </div>
                        <span className="chip">{venueRinks.length} rink{venueRinks.length === 1 ? '' : 's'}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="card">
              <div className="card__head">
                <h3 className="card__title">Up next on every rink</h3>
                <p className="card__sub">A glance at the queue.</p>
              </div>
              {upcomingCount === 0 ? (
                <div className="empty">No upcoming games.</div>
              ) : (
                <div className="stack">
                  {games
                    .filter((g) => g.status === 'scheduled' || g.status === 'ready')
                    .slice(0, 6)
                    .map((g) => {
                      const home = teams.find((t) => t.id === g.homeTeamId);
                      const away = teams.find((t) => t.id === g.awayTeamId);
                      const slot = g.scheduleSlotId ? slotById.get(g.scheduleSlotId) : null;
                      const rink = slot ? db.rinks.find((r) => r.id === slot.rinkId) : null;
                      return (
                        <div key={g.id} className="row row--between" style={{ borderBottom: '1px solid var(--surface-line)', paddingBottom: 6 }}>
                          <div>
                            <strong>{home?.name}</strong> vs <strong>{away?.name}</strong>
                            <div className="muted" style={{ fontSize: 12 }}>{slot ? new Date(slot.startsAt).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : 'Time TBD'} · {rink?.name ?? '—'}</div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card__head">
              <h3 className="card__title">Help & rules</h3>
              <p className="card__sub">The basics, in plain language.</p>
            </div>
            <div className="stack">
              <div>
                <strong>Where do I check in?</strong>
                <p className="muted" style={{ marginTop: 2 }}>The Front Desk handles registration. Captains can confirm rosters in their dashboard.</p>
              </div>
              <div>
                <strong>How long are games?</strong>
                <p className="muted" style={{ marginTop: 2 }}>{tournament.settings.scoring.gameLengthMinutes} minutes — running clock.</p>
              </div>
              <div>
                <strong>Who do I contact for help?</strong>
                <p className="muted" style={{ marginTop: 2 }}>Tournament staff. Look for someone in the bright vest, or check the announcements above.</p>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
